import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  browseFolder,
  syncFolder,
  syncFiles,
  startBatchAnalysis,
  getBatchJobStatus,
  downloadFile,
  deleteFile,
  analyzeAllRecursive,
  searchFiles,
  findFilePage,
  getMCPConfigs,
  previewFilteredBatch,
  startFilteredBatch
} from '../services/folder';
import './FolderBrowserPage.css';

const MAX_SELECTED_FOLDERS = 10;
const normalizePath = (p) => (p || '').replace(/^\/+|\/+$/g, '');

const PAGE_SIZE = 100;

const FolderBrowserPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPath = searchParams.get('path') || '/';
  const highlightFile = searchParams.get('highlight');
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState(new Map()); // path -> {file_hash, name}
  const [selectedFolders, setSelectedFolders] = useState(new Set());
  const [batchJob, setBatchJob] = useState(null);
  const [mcp, setMcp] = useState(() => searchParams.get('mcp') === 'legacy' ? 'legacy' : 'primary');
  const [mcpConfigs, setMcpConfigs] = useState({ primary: { enabled: false }, legacy: { enabled: false } });
  const isLegacy = mcp === 'legacy';
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterForm, setFilterForm] = useState({ modified_from: '', modified_to: '', subfolder: '', only_unanalyzed: true });
  const [filterPreviewCount, setFilterPreviewCount] = useState(null);
  const [filterPreviewLoading, setFilterPreviewLoading] = useState(false);
  const [filterStarting, setFilterStarting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const highlightRef = useRef(null);
  const pollErrorCount = useRef(0);
  const pendingPageRef = useRef(null);

  const loadFolder = useCallback(async (path, pageNum = 0) => {
    setLoading(true);
    setError('');
    try {
      const data = await browseFolder(path, pageNum * PAGE_SIZE, PAGE_SIZE, mcp);
      setEntries(data.items);
      setTotalItems(data.total);
      setPage(pageNum);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar la carpeta');
    } finally {
      setLoading(false);
    }
  }, [mcp]);

  useEffect(() => {
    getMCPConfigs().then(setMcpConfigs).catch(() => {});
  }, []);

  const handleSwitchMcp = (newMcp) => {
    if (newMcp === mcp) return;
    setMcp(newMcp);
    setCurrentPath('/');
    setSelectedFiles(new Map());
    setSelectedFolders(new Set());
  };

  const buildFilterPayload = () => ({
    path: filterForm.subfolder || currentPath || '',
    modified_from: filterForm.modified_from || null,
    modified_to: filterForm.modified_to || null,
    only_unanalyzed: filterForm.only_unanalyzed,
  });

  useEffect(() => {
    if (!showFilterModal) return;
    setFilterPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await previewFilteredBatch(buildFilterPayload(), mcp);
        setFilterPreviewCount(res.count);
      } catch {
        setFilterPreviewCount(null);
      } finally {
        setFilterPreviewLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilterModal, filterForm, currentPath]);

  const handleStartFilteredBatch = async () => {
    setFilterStarting(true);
    try {
      const job = await startFilteredBatch(buildFilterPayload(), mcp);
      setBatchJob(job);
      setShowFilterModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar el análisis filtrado');
    } finally {
      setFilterStarting(false);
    }
  };

  useEffect(() => {
    const targetPage = pendingPageRef.current ?? 0;
    pendingPageRef.current = null;
    setPage(targetPage);
    loadFolder(currentPath, targetPage);
  }, [currentPath, loadFolder]);

  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 3) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await searchFiles(trimmed, 50, 0, mcp);
        setSearchResults(data);
      } catch (err) {
        setSearchResults({ results: [], total: 0, query: trimmed });
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, mcp]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const goToPage = (target) => {
    const clamped = Math.max(0, Math.min(target, totalPages - 1));
    if (clamped !== page) loadFolder(currentPath, clamped);
  };

  const getPageNumbers = () => {
    const window = 5;
    const half = Math.floor(window / 2);
    let start = Math.max(0, page - half);
    let end = Math.min(totalPages - 1, start + window - 1);
    start = Math.max(0, end - window + 1);
    const pages = [];
    for (let i = start; i <= end; i += 1) pages.push(i);
    return { pages, start, end };
  };

  const renderPagination = (key) => {
    if (totalItems <= PAGE_SIZE) return null;
    const { pages, start, end } = getPageNumbers();
    return (
      <div className="pagination" key={key}>
        <button onClick={() => goToPage(page - 1)} disabled={page === 0 || loading} className="pagination-nav">
          ← Anterior
        </button>
        {start > 0 && (
          <>
            <button onClick={() => goToPage(0)} disabled={loading} className="pagination-page">1</button>
            {start > 1 && <span className="pagination-ellipsis">…</span>}
          </>
        )}
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => goToPage(p)}
            disabled={loading}
            className={`pagination-page ${p === page ? 'active' : ''}`}
          >
            {p + 1}
          </button>
        ))}
        {end < totalPages - 1 && (
          <>
            {end < totalPages - 2 && <span className="pagination-ellipsis">…</span>}
            <button onClick={() => goToPage(totalPages - 1)} disabled={loading} className="pagination-page">{totalPages}</button>
          </>
        )}
        <button onClick={() => goToPage(page + 1)} disabled={(page + 1) * PAGE_SIZE >= totalItems || loading} className="pagination-nav">
          Siguiente →
        </button>
        <span className="pagination-info">{totalItems} elementos</span>
      </div>
    );
  };

  const handleOpenSearchResult = async (result) => {
    setSearchQuery('');
    setSearchResults(null);
    const folder = '/' + normalizePath(result.folder_path);
    let targetPage = 0;
    try {
      const data = await findFilePage(folder, result.filename, PAGE_SIZE);
      targetPage = data.page || 0;
    } catch (err) {
      // Si falla, navegamos a la primera página
    }
    pendingPageRef.current = targetPage;
    setSearchParams({ path: folder, highlight: result.filename });
    if (folder === currentPath) {
      setPage(targetPage);
      loadFolder(folder, targetPage);
    } else {
      setCurrentPath(folder);
    }
  };

  useEffect(() => {
    if (highlightFile && !loading && entries.length > 0 && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('highlight');
          return next;
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [highlightFile, loading, entries]);

  // Actualizar hashes de archivos seleccionados cuando cambia la página
  useEffect(() => {
    if (selectedFiles.size > 0 && entries.length > 0) {
      let hasUpdates = false;
      const updatedMap = new Map(selectedFiles);
      entries.forEach(entry => {
        if (updatedMap.has(entry.path)) {
          const current = updatedMap.get(entry.path);
          if (!current.file_hash && entry.file_hash) {
            updatedMap.set(entry.path, { file_hash: entry.file_hash, name: entry.name });
            hasUpdates = true;
          }
        }
      });
      if (hasUpdates) {
        setSelectedFiles(updatedMap);
      }
    }
  }, [entries]);

  // Poll batch job status
  useEffect(() => {
    if (batchJob && (batchJob.status === 'materializing' || batchJob.status === 'pending' || batchJob.status === 'processing')) {
      const interval = setInterval(async () => {
        try {
          const hashes = batchJob.hashes || [batchJob.hash];
          const statuses = await Promise.all(hashes.map(h => getBatchJobStatus(h)));
          const aggregated = {
            hashes: batchJob.hashes,
            hash: batchJob.hash,
            total_files: statuses.reduce((sum, s) => sum + s.total_files, 0),
            completed_files: statuses.reduce((sum, s) => sum + s.completed_files, 0),
            failed_files: statuses.reduce((sum, s) => sum + s.failed_files, 0),
            current_file: statuses.find(s => s.current_file)?.current_file,
            status: statuses.some(s => s.status === 'materializing')
              ? 'materializing'
              : statuses.every(s => s.status === 'completed' || s.status === 'failed')
              ? 'completed'
              : statuses.some(s => s.status === 'processing') ? 'processing' : 'pending',
          };
          setBatchJob(aggregated);

          const allItems = statuses.flatMap(s => s.items || []);
          if (allItems.length > 0) {
            setEntries(prev => prev.map(entry => {
              if (entry.type !== 'file') return entry;
              const match = allItems.find(item =>
                item.filename === entry.name &&
                normalizePath(item.folder_path) === normalizePath(currentPath)
              );
              if (!match) return entry;
              const newStatus =
                match.status === 'completed' ? 'completed' :
                match.status === 'failed' ? 'failed' :
                match.status === 'processing' ? 'analyzing' :
                match.status === 'skipped' ? 'skipped' :
                entry.sync_status;
              if (newStatus === entry.sync_status && match.analysis_hash === entry.analysis_hash) return entry;
              // Si falló, limpiar analysis_hash para no mostrar preview anterior
              const newAnalysisHash = newStatus === 'failed' ? null : (match.analysis_hash || entry.analysis_hash);
              return { ...entry, sync_status: newStatus, analysis_hash: newAnalysisHash };
            }));
          }
          pollErrorCount.current = 0;

          if (aggregated.status !== 'materializing' && aggregated.status !== 'pending' && aggregated.status !== 'processing') {
            clearInterval(interval);
            loadFolder(currentPath);
          }
        } catch (err) {
          console.error('Error polling batch status:', err);
          pollErrorCount.current += 1;
          if (pollErrorCount.current >= 3) {
            clearInterval(interval);
            setError('Se perdió la conexión con el servidor. Recarga la página para ver el estado actual.');
            setBatchJob(prev => prev ? { ...prev, status: 'error' } : null);
          }
        }
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [batchJob, currentPath, loadFolder]);

  const handleNavigate = (entry) => {
    if (entry.type === 'directory') {
      setCurrentPath(entry.path);
      setSelectedFiles(new Map());
      setSelectedFolders(new Set());
    }
  };

  const handleGoUp = () => {
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    setCurrentPath('/' + parts.join('/') || '/');
    setSelectedFiles(new Map());
    setSelectedFolders(new Set());
  };

  const handleSelectFolder = (entry, e) => {
    e.stopPropagation();
    if (entry.type !== 'directory') return;
    if (entry.name === 'Analisis') return;

    setSelectedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entry.path)) {
        newSet.delete(entry.path);
      } else if (newSet.size < MAX_SELECTED_FOLDERS) {
        newSet.add(entry.path);
      }
      return newSet;
    });
  };

  const isAnalyzableFile = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['pdf', 'jpg', 'jpeg', 'png'].includes(ext);
  };

  const handleSelectFile = (entry) => {
    if (entry.type !== 'file') return;
    if (!isAnalyzableFile(entry.name)) return;

    setSelectedFiles(prev => {
      const newMap = new Map(prev);
      if (newMap.has(entry.path)) {
        newMap.delete(entry.path);
      } else {
        newMap.set(entry.path, { file_hash: entry.file_hash, name: entry.name });
      }
      return newMap;
    });
  };

  const handleSelectAll = () => {
    const pendingFiles = entries.filter(e =>
      e.type === 'file' &&
      isAnalyzableFile(e.name) &&
      (!e.sync_status || e.sync_status === 'pending' || e.sync_status === 'not_synced' || e.sync_status === 'failed')
    );
    const newMap = new Map(selectedFiles);
    pendingFiles.forEach(f => newMap.set(f.path, { file_hash: f.file_hash, name: f.name }));
    setSelectedFiles(newMap);
  };

  const handleDeselectAll = () => {
    setSelectedFiles(new Map());
    setSelectedFolders(new Set());
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncFolder(currentPath, mcp);
      const data = await browseFolder(currentPath, page * PAGE_SIZE, PAGE_SIZE);
      setEntries(data.items);
      setTotalItems(data.total);

      if (selectedFiles.size > 0) {
        const updatedMap = new Map(selectedFiles);
        data.items.forEach(entry => {
          if (updatedMap.has(entry.path) && entry.file_hash) {
            updatedMap.set(entry.path, { file_hash: entry.file_hash, name: entry.name });
          }
        });
        setSelectedFiles(updatedMap);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const handleStartBatch = async () => {
    if (selectedFiles.size === 0) return;

    try {
      const pathsWithoutHash = Array.from(selectedFiles.entries())
        .filter(([, info]) => !info.file_hash)
        .map(([path]) => path);

      let updatedFiles = selectedFiles;
      if (pathsWithoutHash.length > 0) {
        const result = await syncFiles(pathsWithoutHash, mcp);
        updatedFiles = new Map(selectedFiles);
        Object.entries(result.file_hashes).forEach(([path, hash]) => {
          const existing = updatedFiles.get(path);
          if (existing) updatedFiles.set(path, { ...existing, file_hash: hash });
        });
        setSelectedFiles(updatedFiles);
      }

      const fileHashes = Array.from(updatedFiles.values())
        .map(info => info.file_hash)
        .filter(Boolean);

      if (fileHashes.length === 0) {
        setError('No hay archivos para analizar.');
        return;
      }

      const job = await startBatchAnalysis(fileHashes);
      setBatchJob(job);
      setSelectedFiles(new Map());
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar el analisis por lotes');
    }
  };

  const handleViewAnalysis = (analysisHash) => {
    navigate(`/history?hash=${analysisHash}`);
  };

  const handleDownloadFile = async (entry) => {
    try {
      const blob = await downloadFile(entry.path, mcp);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = entry.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error al descargar el archivo');
    }
  };

  const handleDeleteFile = async (entry) => {
    if (!window.confirm(`¿Eliminar "${entry.name}"?`)) return;
    try {
      await deleteFile(entry.path);
      loadFolder(currentPath);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error desconocido';
      setError(`Error al eliminar: ${detail}`);
    }
  };

  const isExcelFile = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['xlsx', 'xls'].includes(ext);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'not_synced': { label: 'Nuevo', className: 'status-new' },
      'pending': { label: 'Pendiente', className: 'status-pending' },
      'queued': { label: 'En cola', className: 'status-queued' },
      'analyzing': { label: 'Analizando', className: 'status-processing' },
      'completed': { label: 'Completado', className: 'status-completed' },
      'failed': { label: 'Error', className: 'status-failed' },
      'skipped': { label: 'No es plano', className: 'status-skipped' },
      'invoice': { label: 'Factura/Albarán', className: 'status-invoice' },
    };
    const info = statusMap[status] || { label: status || 'Nuevo', className: 'status-new' };
    return <span className={`status-badge ${info.className}`}>{info.label}</span>;
  };

  const getBreadcrumbParts = () => {
    return currentPath.split('/').filter(Boolean);
  };

  const pendingCount = entries.filter(e =>
    e.type === 'file' &&
    isAnalyzableFile(e.name) &&
    (!e.sync_status || e.sync_status === 'pending' || e.sync_status === 'not_synced' || e.sync_status === 'failed')
  ).length;

  const progressPercent = batchJob ? Math.round((batchJob.completed_files / batchJob.total_files) * 100) : 0;

  const handleAnalyzeSelectedFolders = async () => {
    if (selectedFolders.size === 0) return;

    setAnalyzingAll(true);
    const paths = Array.from(selectedFolders);
    let totalQueued = 0;
    const batchHashes = [];
    const errors = [];

    for (let i = 0; i < paths.length; i++) {
      try {
        const result = await analyzeAllRecursive(paths[i], true, mcp);
        if (result.batch_job_hash) {
          batchHashes.push(result.batch_job_hash);
          totalQueued += result.files_queued;
        }
        if (i < paths.length - 1) {
          await new Promise(r => setTimeout(r, 1500));
        }
      } catch (err) {
        errors.push(paths[i]);
      }
    }

    setSelectedFolders(new Set());

    if (batchHashes.length > 0) {
      setBatchJob({
        hashes: batchHashes,
        hash: batchHashes[batchHashes.length - 1],
        status: 'pending',
        total_files: totalQueued,
        completed_files: 0,
        failed_files: 0
      });
    }
    loadFolder(currentPath);

    if (errors.length > 0) {
      setError(`Error al iniciar análisis en ${errors.length} carpeta(s): ${errors.join(', ')}`);
    }

    setAnalyzingAll(false);
  };

  const hasSelection = selectedFiles.size > 0 || selectedFolders.size > 0;

  return (
    <div className="folder-browser-page">
      <div className="page-header">
        <h1>Explorador de Carpeta</h1>
        <div className="header-actions">
          {!isLegacy && selectedFiles.size > 0 && (
            <button onClick={handleStartBatch} className="btn-primary">
              Analizar {selectedFiles.size} archivo(s)
            </button>
          )}
          {!isLegacy && selectedFolders.size > 0 && (
            <button onClick={handleAnalyzeSelectedFolders} className="btn-primary" disabled={analyzingAll}>
              {analyzingAll ? 'Iniciando análisis...' : `Analizar ${selectedFolders.size} carpeta(s)`}
            </button>
          )}
          {hasSelection && (
            <button onClick={handleDeselectAll} className="btn-secondary">
              Limpiar selección
            </button>
          )}
        </div>
      </div>

      {mcpConfigs.legacy?.enabled && (
        <div className="mcp-selector">
          <button
            className={`mcp-tab ${mcp === 'primary' ? 'active' : ''}`}
            onClick={() => handleSwitchMcp('primary')}
            disabled={!mcpConfigs.primary?.enabled}
          >
            Carpeta activa
          </button>
          <button
            className={`mcp-tab ${mcp === 'legacy' ? 'active' : ''}`}
            onClick={() => handleSwitchMcp('legacy')}
          >
            Carpeta legacy (solo lectura)
          </button>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {batchJob && batchJob.status === 'materializing' && (
        <div className="batch-progress batch-progress--materializing">
          <div className="batch-progress-header">
            <h3>Preparando ingesta...</h3>
            <span className="batch-status">
              <span className="spinner" /> Localizando archivos
            </span>
          </div>
          <p className="progress-text">
            Estamos seleccionando y registrando los archivos que cumplen los filtros. Este paso puede tardar varios minutos para tandas grandes.
          </p>
        </div>
      )}

      {batchJob && (batchJob.status === 'pending' || batchJob.status === 'processing') && (
        <div className="batch-progress">
          <div className="batch-progress-header">
            <h3>Procesamiento por lotes</h3>
            <span className="batch-status">{batchJob.status === 'pending' ? 'Iniciando...' : 'Procesando...'}</span>
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="progress-text">
            {batchJob.completed_files} / {batchJob.total_files} completados
            {batchJob.failed_files > 0 && <span className="failed-count"> ({batchJob.failed_files} fallidos)</span>}
          </p>
          {batchJob.current_file && (
            <p className="current-file">Analizando: <strong>{batchJob.current_file}</strong></p>
          )}
        </div>
      )}

      {batchJob && batchJob.status === 'completed' && (
        <div className="batch-completed">
          <p>
            Lote completado: {batchJob.completed_files} exitosos
            {batchJob.failed_files > 0 && `, ${batchJob.failed_files} fallidos`}
          </p>
          <button onClick={() => setBatchJob(null)} className="btn-secondary btn-small">
            Cerrar
          </button>
        </div>
      )}

      <div className="breadcrumb">
        <button onClick={() => setCurrentPath('/')} className="breadcrumb-item">
          Raiz
        </button>
        {getBreadcrumbParts().map((part, idx, arr) => (
          <React.Fragment key={idx}>
            <span className="breadcrumb-separator">/</span>
            <button
              onClick={() => setCurrentPath('/' + arr.slice(0, idx + 1).join('/'))}
              className="breadcrumb-item"
            >
              {part}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="folder-search">
        <input
          type="text"
          className="folder-search-input"
          placeholder="Buscar archivo por nombre (mín. 3 caracteres)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button
            type="button"
            className="folder-search-clear"
            onClick={() => setSearchQuery('')}
          >
            ×
          </button>
        )}
      </div>

      {searchQuery.trim().length >= 3 && (
        <div className="folder-search-results">
          {searching && <div className="loading">Buscando...</div>}
          {!searching && searchResults && searchResults.results.length === 0 && (
            <div className="empty-folder">Sin coincidencias para "{searchResults.query}"</div>
          )}
          {!searching && searchResults && searchResults.results.length > 0 && (
            <>
              <div className="search-summary">
                {searchResults.total} resultado{searchResults.total !== 1 ? 's' : ''}
              </div>
              <ul className="search-results-list">
                {searchResults.results.map((r) => (
                  <li key={r.hash} className="search-result-item" onClick={() => handleOpenSearchResult(r)}>
                    <span className="search-result-name">{r.filename}</span>
                    <span className="search-result-path">{r.folder_path}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="folder-actions">
        {currentPath !== '/' && (
          <button onClick={handleGoUp} className="btn-secondary">
            Subir
          </button>
        )}
        {!isLegacy && (
          <button onClick={handleSync} className="btn-secondary" disabled={syncing}>
            {syncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        )}
        {!isLegacy && (
          <button onClick={() => { setShowFilterModal(true); setFilterPreviewCount(null); }} className="btn-secondary">
            Analizar con filtros
          </button>
        )}
        {!isLegacy && pendingCount > 0 && (
          <>
            <button onClick={handleSelectAll} className="btn-secondary">
              Seleccionar pendientes ({pendingCount})
            </button>
            {selectedFiles.size > 0 && (
              <button onClick={handleDeselectAll} className="btn-secondary">
                Deseleccionar todo
              </button>
            )}
          </>
        )}
      </div>

      {renderPagination('top')}

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : (
        <div className="file-list">
          {entries.length === 0 ? (
            <div className="empty-folder">Carpeta vacia</div>
          ) : (
            entries.map((entry, idx) => (
              <div
                key={idx}
                ref={highlightFile && entry.name === highlightFile ? highlightRef : null}
                className={`file-item ${entry.type} ${selectedFiles.has(entry.path) ? 'selected' : ''} ${selectedFolders.has(entry.path) ? 'selected' : ''} ${highlightFile && entry.name === highlightFile ? 'highlight-flash' : ''}`}
                onClick={() => entry.type === 'directory' ? handleNavigate(entry) : handleSelectFile(entry)}
              >
                {entry.type === 'directory' && entry.name !== 'Analisis' && (
                  <input
                    type="checkbox"
                    className="folder-checkbox"
                    checked={selectedFolders.has(entry.path)}
                    onChange={(e) => handleSelectFolder(entry, e)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={!selectedFolders.has(entry.path) && selectedFolders.size >= MAX_SELECTED_FOLDERS}
                    title={selectedFolders.size >= MAX_SELECTED_FOLDERS && !selectedFolders.has(entry.path) ? `Máximo ${MAX_SELECTED_FOLDERS} carpetas` : ''}
                  />
                )}
                <span className="file-icon">
                  {entry.type === 'directory' ? '📁' : '📄'}
                </span>
                <span className="file-name">
                  {entry.name}
                  {entry.type === 'directory' && entry.name === 'Analisis' && (
                    <span className="folder-badge folder-badge-results">Resultados</span>
                  )}
                </span>
                {entry.type === 'file' && (
                  <>
                    {!isExcelFile(entry.name) && (
                      <span className="file-status">
                        {getStatusBadge(entry.sync_status)}
                      </span>
                    )}
                    {isExcelFile(entry.name) && (
                      <button
                        className="download-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadFile(entry);
                        }}
                      >
                        Descargar
                      </button>
                    )}
                    {entry.analysis_hash && (
                      <button
                        className="view-analysis-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewAnalysis(entry.analysis_hash);
                        }}
                      >
                        Ver analisis
                      </button>
                    )}
                    {!isLegacy && (
                      <button
                        className="delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFile(entry);
                        }}
                        title="Eliminar"
                      >
                        🗑️
                      </button>
                    )}
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {renderPagination('bottom')}

      {showFilterModal && (
        <div className="modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="filter-modal-header">
              <h3>Analizar con filtros</h3>
              <button className="modal-close" onClick={() => setShowFilterModal(false)}>×</button>
            </div>
            <div className="filter-modal-body">
              <p className="form-hint">
                Filtra archivos ya sincronizados de la carpeta activa. Solo se analizarán los que cumplan TODOS los filtros.
              </p>
              <div className="form-group">
                <label>Subcarpeta (opcional)</label>
                <input
                  type="text"
                  value={filterForm.subfolder}
                  onChange={(e) => setFilterForm({ ...filterForm, subfolder: e.target.value })}
                  placeholder={`Por defecto: ${currentPath}`}
                />
              </div>
              <div className="form-group filter-form-row">
                <div>
                  <label>Modificados desde</label>
                  <input
                    type="date"
                    value={filterForm.modified_from}
                    onChange={(e) => setFilterForm({ ...filterForm, modified_from: e.target.value })}
                  />
                </div>
                <div>
                  <label>Modificados hasta</label>
                  <input
                    type="date"
                    value={filterForm.modified_to}
                    onChange={(e) => setFilterForm({ ...filterForm, modified_to: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={filterForm.only_unanalyzed}
                    onChange={(e) => setFilterForm({ ...filterForm, only_unanalyzed: e.target.checked })}
                  />
                  {' '}Solo archivos aún no analizados
                </label>
              </div>
              <div className="filter-preview">
                {filterPreviewLoading ? 'Calculando...' : (
                  filterPreviewCount !== null
                    ? <strong>{filterPreviewCount}</strong>
                    : '—'
                )} archivos coinciden con los filtros
              </div>
            </div>
            <div className="filter-modal-footer">
              <button className="btn-secondary" onClick={() => setShowFilterModal(false)} disabled={filterStarting}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleStartFilteredBatch}
                disabled={filterStarting || !filterPreviewCount}
              >
                {filterStarting ? 'Iniciando...' : `Analizar ${filterPreviewCount || 0} archivo(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderBrowserPage;
